import type { PlaybookTree } from "@repo/app-types/entities/playbook";
import type { RecordingAssessmentFeedbackStatus } from "@repo/app-types/entities/recordingAssessmentFeedback";
import type { FC } from "react";
import { useSearchParams } from "react-router";
import { appPaths } from "../app/appPaths.js";
import { RouteTabs, type RouteTabItem } from "./layout/routeTabs.js";
import {
  checkIsLiveFeedbackSelection,
  parseFeedbackStatus,
  parsePlaybookEditorSelectionId,
  PLAYBOOK_FEEDBACK_STATUS_QUERY_PARAM,
  PLAYBOOK_SELECTION_QUERY_PARAM,
  type PlaybookEditorSelectionId,
} from "./playbookEditorUrlState.js";

export interface PlaybookEditorRouteTabsProps {
  playbookIdParam: string;
  canReviewFeedback: boolean;
  liveTree: PlaybookTree | undefined;
  lastStableSelectionId: PlaybookEditorSelectionId | null;
  reviewStatus: RecordingAssessmentFeedbackStatus;
}

export const PlaybookEditorRouteTabs: FC<PlaybookEditorRouteTabsProps> = (
  props,
) => {
  const {
    playbookIdParam,
    canReviewFeedback,
    liveTree,
    lastStableSelectionId,
    reviewStatus,
  } = props;
  const [searchParams] = useSearchParams();

  const tabSelectionId =
    parsePlaybookEditorSelectionId(
      searchParams.get(PLAYBOOK_SELECTION_QUERY_PARAM),
    ) ?? lastStableSelectionId;
  const tabReviewStatus = searchParams.has(PLAYBOOK_FEEDBACK_STATUS_QUERY_PARAM)
    ? parseFeedbackStatus(
        searchParams.get(PLAYBOOK_FEEDBACK_STATUS_QUERY_PARAM),
      )
    : reviewStatus;

  const editSearchParams = new URLSearchParams();
  if (tabSelectionId) {
    editSearchParams.set(PLAYBOOK_SELECTION_QUERY_PARAM, tabSelectionId);
  }
  const feedbackSearchParams = new URLSearchParams();
  if (checkIsLiveFeedbackSelection(tabSelectionId, liveTree)) {
    feedbackSearchParams.set(PLAYBOOK_SELECTION_QUERY_PARAM, tabSelectionId);
  }
  feedbackSearchParams.set(
    PLAYBOOK_FEEDBACK_STATUS_QUERY_PARAM,
    tabReviewStatus,
  );

  const tabs: RouteTabItem[] = [
    {
      label: "Playbook",
      to: {
        pathname: appPaths.settingsPlaybookEdit.build({
          playbookId: playbookIdParam,
        }),
        search: getSearchString(editSearchParams),
      },
    },
    ...(canReviewFeedback
      ? [
          {
            label: "Review feedback",
            to: {
              pathname: appPaths.settingsPlaybookFeedback.build({
                playbookId: playbookIdParam,
              }),
              search: getSearchString(feedbackSearchParams),
            },
          },
        ]
      : []),
    {
      label: "Settings",
      to: appPaths.settingsPlaybookSettings.build({
        playbookId: playbookIdParam,
      }),
    },
  ];

  return <RouteTabs tabs={tabs} ariaLabel="Playbook editor tabs" />;
};

function getSearchString(searchParams: URLSearchParams): string | undefined {
  const query = searchParams.toString();
  return query ? `?${query}` : undefined;
}
